import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb, { getCategoryName } from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getArticlesByCategory, getHotArticles } from '@/mocks/articles';
import { getArticleImage } from '@/mocks/images';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = getCategoryName(slug || '');
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = getArticlesByCategory(slug || '');
  const hotArticles = getHotArticles();

  if (!category || categoryArticles.length === 0) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">分类不存在</h1>
            <p className="text-foreground-600 mb-6">该分类下暂无内容</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 爆料网`,
    description: category.description,
    url: `https://baoliaowang.com/category/${slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>

      <Navbar />

      <main>
        {/* Category Header */}
        <section className="pt-20 pb-8 px-6 md:px-10 bg-background-100/60">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
            <div className="mt-4 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center">
                <i className={`${category.icon} text-2xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="py-4 px-6 md:px-10 border-b border-background-200/70 bg-background-50 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto flex items-center gap-2 overflow-x-auto">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors"
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Content */}
        <section className="py-10 md:py-14 px-6 md:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main */}
              <div className="flex-1">
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                  共 {categoryArticles.length} 篇{categoryName}相关爆料
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {categoryArticles.map((article) => (
                    <ArticleCard
                      key={article.slug}
                      slug={article.slug}
                      title={article.title}
                      excerpt={article.excerpt}
                      category={article.category}
                      categoryName={categoryName}
                      imageUrl={getArticleImage(article.slug, 'card')}
                      author={article.author}
                      date={article.date}
                      views={article.views}
                      tags={article.tags}
                    />
                  ))}
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:w-[320px] flex-shrink-0">
                <div className="sticky top-32">
                  <div className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-fire-fill text-accent-500"></i>
                      热门爆料
                    </h3>
                    <div className="space-y-1">
                      {hotArticles.map((article) => (
                        <ArticleCard
                          key={article.slug}
                          slug={article.slug}
                          title={article.title}
                          excerpt={article.excerpt}
                          category={article.category}
                          categoryName={categories.find((c) => c.slug === article.category)?.name || ''}
                          imageUrl={getArticleImage(article.slug, 'compact')}
                          author={article.author}
                          date={article.date}
                          views={article.views}
                          tags={article.tags}
                          variant="compact"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}